import { useState, useRef, useEffect } from "react";
import { useHarelBatchLoader } from "../hooks/useHarelBatchLoader";
import { SOURCE_TYPE } from "../utils/dataSource";
import { monthToValueDate } from "../utils/valueMonth";

const fmt = n => n ? n.toLocaleString('he-IL') : '0';

function BalancePill({ funds }) {
  if (!funds?.length) return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;
  const total = funds.reduce((s, f) => s + f.balance, 0);
  return (
    <span style={{ fontSize: 11, color: '#b45309', fontWeight: 700, direction: 'ltr' }}>
      ₪{fmt(total)}
    </span>
  );
}

function FundList({ funds }) {
  if (!funds?.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2 }}>
      {funds.map((f, i) => (
        <span key={i} style={{ fontSize: 10, color: '#64748b' }}>
          {f.name}: <span style={{ direction: 'ltr', display: 'inline-block' }}>₪{fmt(f.balance)}</span>
        </span>
      ))}
    </div>
  );
}

// Step 1 – Upload
function UploadStep({ onFiles, busy }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const dragCounter = useRef(0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        onDragEnter={e => { e.preventDefault(); dragCounter.current++; setDragging(true); }}
        onDragLeave={e => { e.preventDefault(); if (--dragCounter.current === 0) setDragging(false); }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          dragCounter.current = 0;
          setDragging(false);
          if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
        }}
        style={{
          border: `2px dashed ${dragging ? '#d97706' : '#cbd5e1'}`,
          borderRadius: 12, padding: '40px 24px',
          background: dragging ? '#fffbeb' : '#f8fafc',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 10, cursor: 'default',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 48 }}>{dragging ? '📂' : '🦁'}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: dragging ? '#d97706' : '#475569' }}>
          {dragging ? 'שחרר כאן' : 'גרור קבצי פרט של הראל'}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.6 }}>
          קבצים: <span style={{ fontFamily: 'monospace' }}>RP*</span>,{' '}
          <span style={{ fontFamily: 'monospace' }}>SB*</span>,{' '}
          <span style={{ fontFamily: 'monospace' }}>SP*</span>,{' '}
          <span style={{ fontFamily: 'monospace' }}>RM*.R</span>
          <br />
          ניתן לגרור את כל הקבצים יחד, או ZIP המכיל אותם
        </div>
      </div>

      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        style={{
          padding: '11px 0', background: busy ? '#fde68a' : '#d97706',
          color: 'white', border: 'none', borderRadius: 8,
          cursor: busy ? 'wait' : 'pointer', fontWeight: 700, fontSize: 14,
        }}
      >
        {busy ? '⏳ מנתח…' : 'בחר קבצים מהמחשב'}
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}

// Step 2 – Review
function ReviewStep({ results, onConfirm, onBack, onUpdateNewName, monthValues, onUpdateMonth }) {
  const totalMatched   = results.reduce((s, r) => s + (r.matched?.length   || 0), 0);
  const totalUnmatched = results.reduce((s, r) => s + (r.unmatched?.length || 0), 0);
  const errors = results.filter(r => r.error);
  const monthsReady = results.every((r, i) => r.error || monthValues[i] || r.defaultReportMonth);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '65vh', overflow: 'hidden' }}>
      {/* Summary */}
      <div style={{
        display: 'flex', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: '#fffbeb',
        borderRadius: 8, fontSize: 12,
      }}>
        <span>✅ <strong>{totalMatched}</strong> לקוחות קיימים</span>
        {totalUnmatched > 0 && <span>🆕 <strong>{totalUnmatched}</strong> לקוחות חדשים</span>}
        {errors.length > 0 && <span style={{ color: '#dc2626' }}>❌ {errors.length} שגיאות</span>}
      </div>

      <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {results.map((res, ri) => {
          if (res.error) return (
            <div key={ri} style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#dc2626' }}>
              ❌ {res.fileName}: {res.error}
            </div>
          );

          return (
            <div key={ri}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>🦁 {res.fileName}</span>
                <span style={{ fontWeight: 400, color: '#64748b' }}>
                  · {(res.matched?.length || 0) + (res.unmatched?.length || 0)} חברים
                </span>
                {/* Year isn't in the Harel filename — let the user set the report month */}
                <label style={{ fontWeight: 400, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  חודש הדוח:
                  <input
                    type="month"
                    value={monthValues[ri] || res.defaultReportMonth || ''}
                    onChange={e => onUpdateMonth(ri, e.target.value)}
                    style={{ fontSize: 12, padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4 }}
                  />
                </label>
              </div>

              {/* Matched */}
              {res.matched?.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 8 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', color: '#64748b' }}>
                      <th style={th}>ת"ז</th>
                      <th style={th}>לקוח קיים</th>
                      <th style={th}>יתרה</th>
                      <th style={th}>קרנות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.matched.map((m, mi) => (
                      <tr key={mi} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...td, direction: 'ltr', fontFamily: 'monospace', fontSize: 10 }}>{m.taz}</td>
                        <td style={td}><span style={{ color: '#059669', fontWeight: 600 }}>✓</span> {m.existingName}</td>
                        <td style={td}><BalancePill funds={m.funds} /></td>
                        <td style={td}><FundList funds={m.funds} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Unmatched */}
              {res.unmatched?.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 4 }}>
                    לקוחות ללא שיוך — הנתונים יישמרו, שייך ידנית לאחר הייבוא
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#fffbeb', color: '#64748b' }}>
                        <th style={th}>ת"ז</th>
                        <th style={th}>שם לקוח</th>
                        <th style={th}>יתרה</th>
                        <th style={th}>קרנות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.unmatched.map((m, mi) => (
                        <tr key={mi} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ ...td, direction: 'ltr', fontFamily: 'monospace', fontSize: 10 }}>{m.taz}</td>
                          <td style={td}>
                            <input
                              value={m.newName}
                              onChange={e => onUpdateNewName(ri, mi, e.target.value)}
                              placeholder="שם מלא (ריק = לא ייווצר)"
                              style={{
                                width: '100%', padding: '3px 6px', fontSize: 11,
                                border: '1px solid #d1d5db', borderRadius: 4,
                                boxSizing: 'border-box',
                              }}
                            />
                          </td>
                          <td style={td}><BalancePill funds={m.funds} /></td>
                          <td style={td}><FundList funds={m.funds} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
        <button onClick={onBack} style={{ padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#64748b' }}>
          חזור
        </button>
        <button
          onClick={onConfirm}
          disabled={!monthsReady}
          title={monthsReady ? undefined : 'יש לבחור חודש דוח לכל קובץ'}
          style={{ flex: 1, padding: '10px 0', background: monthsReady ? '#d97706' : '#cbd5e1', color: 'white', border: 'none', borderRadius: 8, cursor: monthsReady ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14 }}
        >
          ✓ אשר ייבוא
        </button>
      </div>
    </div>
  );
}

// Step 3 – Done
function DoneStep({ summary, onClose }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '16px 0' }}>
      <div style={{ fontSize: 48 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#b45309' }}>הייבוא הושלם</div>
      <div style={{ fontSize: 13, color: '#475569', textAlign: 'center' }}>
        {summary.linked  > 0 && <div>✓ {summary.linked} לקוחות שויכו</div>}
        {summary.created > 0 && <div>🆕 {summary.created} לקוחות חדשים נוצרו</div>}
        {summary.skipped > 0 && <div style={{ color: '#f59e0b' }}>◌ {summary.skipped} ממתינים לשיוך ידני</div>}
      </div>
      <button onClick={onClose} style={{ padding: '10px 32px', background: '#d97706', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
        סגור
      </button>
    </div>
  );
}

const th = { textAlign: 'right', padding: '6px 8px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' };
const td = { padding: '5px 8px', verticalAlign: 'top' };

export default function HarelImport({ onClose, existingClients, addImport, initialFiles }) {
  const [step, setStep] = useState('upload');
  const [results, setResults] = useState([]);
  const [monthValues, setMonthValues] = useState({}); // { [resultIdx]: "YYYY-MM" } chosen report month
  const [summary, setSummary] = useState(null);
  const { loadFiles, busy } = useHarelBatchLoader();

  const handleFiles = async (files) => {
    const parsed = await loadFiles(files, existingClients);
    if (parsed.length > 0) {
      setResults(parsed);
      setStep('review');
    }
  };

  useEffect(() => {
    if (initialFiles?.length) handleFiles(initialFiles);
  }, []); // eslint-disable-line

  const handleUpdateMonth = (ri, value) => {
    setMonthValues(prev => ({ ...prev, [ri]: value }));
  };

  const handleUpdateNewName = (ri, mi, name) => {
    setResults(prev => prev.map((r, i) => i !== ri ? r : {
      ...r,
      unmatched: r.unmatched.map((m, j) => j !== mi ? m : { ...m, newName: name }),
    }));
  };

  const handleConfirm = () => {
    let linked = 0, created = 0, skipped = 0;
    const createdByTaz = new Map();

    for (let ri = 0; ri < results.length; ri++) {
      const res = results[ri];
      if (res.error) continue;

      const pick = (m, clientId) => ({
        rawId: m.rawId, taz: m.taz, clientId,
        firstName: m.firstName, lastName: m.lastName,
        city: m.city, gender: m.gender, birthDate: m.birthDate,
        startDate: m.startDate,
        tochnit: m.tochnit || null,
        productType: m.productType || 'savings',
        dmeiTzvPct: m.dmeiTzvPct,
        dmeiHafPct: m.dmeiHafPct,
        funds: m.funds,
        displayName: m.newName?.trim() || undefined,
      });

      const members = [];

      for (const m of res.matched || []) {
        members.push(pick(m, m.existingClientId));
        linked++;
      }

      for (const m of res.unmatched || []) {
        members.push(pick(m, null));
        if (m.newName?.trim()) created++;
        else skipped++;
      }

      const chosenMonth = monthValues[ri] || res.defaultReportMonth; // "YYYY-MM"
      const reportDate = monthToValueDate(chosenMonth) || res.reportDate;
      addImport({ fileName: res.fileName, reportDate, sourceType: SOURCE_TYPE.PRAT, members });
    }

    setSummary({ linked, created, skipped });
    setStep('done');
  };

  return (
    <div
      onClick={busy ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 4000,
        background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        direction: 'rtl',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 16,
          padding: 28, width: step === 'review' ? 700 : 460,
          maxWidth: '95vw', maxHeight: '90vh',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column', gap: 16,
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <button onClick={onClose} disabled={busy} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: busy ? 'default' : 'pointer', color: '#94a3b8' }}>
            ✕
          </button>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
            🦁 ייבוא קבצי הראל
          </h3>
        </div>

        {step === 'upload' && <UploadStep onFiles={handleFiles} busy={busy} />}
        {step === 'review' && (
          <ReviewStep
            results={results}
            onConfirm={handleConfirm}
            onBack={() => setStep('upload')}
            onUpdateNewName={handleUpdateNewName}
            monthValues={monthValues}
            onUpdateMonth={handleUpdateMonth}
          />
        )}
        {step === 'done' && <DoneStep summary={summary} onClose={onClose} />}
      </div>
    </div>
  );
}
